import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../../../components/Loading";
import { OperationsReportsButton, PageHeader } from "../../../components/ui";
import { HrInitiateLink } from "../../../components/hr_operations";
import { api } from "../../../lib/api";
import type { LeaveRequestDetail, LeaveTypeDetail } from "../../../lib/api";
import { useLegalEntityGate } from "../../../lib/legalEntity";

// Leave Calendar (M15.11). Month grid that overlays every approved
// or pending_approval request for the LE. Click a date → see who is
// out. Color-coded by leave_type category.
//
// Routing: /leave/calendar?le=<uuid>

const CATEGORY_COLORS: Record<string, string> = {
  annual: "bg-emerald-500",
  sick: "bg-rose-500",
  emergency: "bg-amber-500",
  hajj: "bg-violet-500",
  maternity: "bg-pink-500",
  paternity: "bg-sky-500",
  bereavement: "bg-slate-500",
  marriage: "bg-fuchsia-500",
  iddah: "bg-teal-500",
  exam: "bg-indigo-500",
  unpaid: "bg-zinc-500",
  hardship_travel: "bg-orange-500",
  other: "bg-slate-400",
};

function _isoMonthOf(d: Date): string {
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}`;
}

function _formatMonthLabel(d: Date): string {
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

interface DayCell {
  date: string; // ISO yyyy-mm-dd
  inMonth: boolean;
  requests: LeaveRequestDetail[];
}

function _buildMonthGrid(
  year: number,
  month0: number, // 0-indexed
  requests: LeaveRequestDetail[],
): DayCell[] {
  const cells: DayCell[] = [];
  const first = new Date(year, month0, 1);
  const firstDow = first.getDay(); // Sunday=0
  // KSA-typical: week starts Sunday — matches default JS getDay.
  for (let i = firstDow; i > 0; i--) {
    const d = new Date(year, month0, 1 - i);
    cells.push({
      date: _toIso(d),
      inMonth: false,
      requests: _coveringRequests(_toIso(d), requests),
    });
  }
  const last = new Date(year, month0 + 1, 0).getDate();
  for (let dayNum = 1; dayNum <= last; dayNum++) {
    const d = new Date(year, month0, dayNum);
    cells.push({
      date: _toIso(d),
      inMonth: true,
      requests: _coveringRequests(_toIso(d), requests),
    });
  }
  // Pad to a full 6-row grid for stable layout.
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const idx = cells.length;
    const d = new Date(year, month0, last + (idx - (firstDow + last - 1)));
    cells.push({
      date: _toIso(d),
      inMonth: false,
      requests: _coveringRequests(_toIso(d), requests),
    });
  }
  return cells.slice(0, 42);
}

function _toIso(d: Date): string {
  return `${d.getFullYear()}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

function _coveringRequests(
  iso: string,
  requests: LeaveRequestDetail[],
): LeaveRequestDetail[] {
  return requests.filter((r) => iso >= r.start_date && iso <= r.end_date);
}

function _colorForRequest(
  r: LeaveRequestDetail,
  types: LeaveTypeDetail[],
): string {
  const lt = types.find((t) => t.id === r.leave_type_id);
  if (!lt) return "bg-slate-400";
  return CATEGORY_COLORS[lt.category] ?? "bg-slate-400";
}

export function LeaveCalendarPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const today = new Date();
  const [month, setMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const requestsQ = useQuery({
    queryKey: ["leave-calendar", le, _isoMonthOf(month)],
    queryFn: async () => {
      const [approved, pending] = await Promise.all([
        api.listLeaveRequests({ legalEntityId: le, state: "approved" }),
        api.listLeaveRequests({
          legalEntityId: le,
          state: "pending_approval",
        }),
      ]);
      return [...approved, ...pending];
    },
    enabled: !!le,
  });

  const typesQ = useQuery({
    queryKey: ["leave-types", le],
    queryFn: () => api.listLeaveTypes(le),
    enabled: !!le,
  });

  const grid = useMemo(() => {
    if (!requestsQ.data) return [];
    return _buildMonthGrid(
      month.getFullYear(),
      month.getMonth(),
      requestsQ.data,
    );
  }, [month, requestsQ.data]);

  if (gate) return gate;
  if (requestsQ.isLoading) return <Loading />;
  if (requestsQ.error)
    return <ErrorPanel message={(requestsQ.error as Error).message} />;

  const types = typesQ.data ?? [];
  const dayDetail = selectedDay
    ? (requestsQ.data ?? []).filter(
        (r) => selectedDay >= r.start_date && selectedDay <= r.end_date,
      )
    : [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("operations.leave_calendar_title")}
        breadcrumbs={[
          { label: t("operations.leave_breadcrumb_ops"), href: "/operations" },
          { label: t("operations.leave_breadcrumb_leave"), href: "/operations/leave/requests" },
          { label: t("operations.leave_breadcrumb_calendar") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <HrInitiateLink transactionType="leave" />
            <OperationsReportsButton
              domain="leaves"
              domainLabel={t("nav.leave", "Leaves")}
            />
            <button
              type="button"
              onClick={() =>
                setMonth(
                  new Date(month.getFullYear(), month.getMonth() - 1, 1),
                )
              }
              aria-label={t("operations.leave_previous_month")}
              className="rounded-sm p-1 text-text-secondary hover:bg-surface-sunken"
            >
              <ChevronLeft size={20} className="rtl:rotate-180" />
            </button>
            <span className="min-w-[10rem] text-center text-sm font-medium">
              {_formatMonthLabel(month)}
            </span>
            <button
              type="button"
              onClick={() =>
                setMonth(
                  new Date(month.getFullYear(), month.getMonth() + 1, 1),
                )
              }
              aria-label={t("operations.leave_next_month")}
              className="rounded-sm p-1 text-text-secondary hover:bg-surface-sunken"
            >
              <ChevronRight size={20} className="rtl:rotate-180" />
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-7 gap-1 rounded-xl border border-slate-200 bg-white p-2">
        {[
          t("operations.leave_cal_day_sun"),
          t("operations.leave_cal_day_mon"),
          t("operations.leave_cal_day_tue"),
          t("operations.leave_cal_day_wed"),
          t("operations.leave_cal_day_thu"),
          t("operations.leave_cal_day_fri"),
          t("operations.leave_cal_day_sat"),
        ].map((d) => (
          <div
            key={d}
            className="px-2 py-1 text-center text-xs font-medium uppercase text-slate-500"
          >
            {d}
          </div>
        ))}
        {grid.map((cell) => {
          const dayLabel = parseInt(cell.date.slice(-2), 10);
          const isSelected = selectedDay === cell.date;
          return (
            <button
              type="button"
              key={cell.date}
              onClick={() => setSelectedDay(cell.date)}
              className={`flex min-h-[5.5rem] flex-col items-stretch rounded-lg border p-1.5 text-start text-xs ${
                cell.inMonth ? "bg-white" : "bg-slate-50 text-slate-400"
              } ${
                isSelected
                  ? "border-slate-900"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <span className="font-medium">{dayLabel}</span>
              {cell.requests.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {cell.requests.slice(0, 6).map((r) => (
                    <span
                      key={r.id}
                      title={`${r.start_date} → ${r.end_date}`}
                      className={`h-2 w-2 rounded-full ${_colorForRequest(
                        r,
                        types,
                      )}`}
                    />
                  ))}
                  {cell.requests.length > 6 && (
                    <span className="text-[10px] text-slate-500">
                      +{cell.requests.length - 6}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-slate-900">
            {t("operations.leave_cal_day_summary", {
              date: selectedDay,
              count: dayDetail.length,
            })}
          </h2>
          {dayDetail.length === 0 ? (
            <p className="text-sm text-slate-500">{t("operations.leave_cal_no_one_out")}</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm">
              {dayDetail.map((r) => {
                const lt = types.find((lt2) => lt2.id === r.leave_type_id);
                // M3X.LIST-AND-REPORT-FIXES PR 2 — render pre-joined
                // employee name. Locale-aware. Falls back to the UUID
                // prefix only when the join missed (orphan row).
                const isAr = (typeof window !== "undefined" && window.document?.documentElement?.dir === "rtl");
                const displayName =
                  (isAr && r.employee_name_ar)
                    ? r.employee_name_ar
                    : r.employee_name_en ?? r.employee_id.slice(0, 8);
                const typeLabel =
                  (isAr && r.leave_type_name_ar) ||
                  r.leave_type_name_en ||
                  lt?.name_en ||
                  t("operations.leave_cal_leave_default");
                return (
                  <li
                    key={r.id}
                    className="flex items-center gap-2 rounded border border-slate-100 p-2"
                  >
                    <span
                      className={`h-3 w-3 rounded-full ${_colorForRequest(
                        r,
                        types,
                      )}`}
                    />
                    <span className="text-sm font-medium text-slate-700">
                      {displayName}
                    </span>
                    <span className="text-xs text-slate-500">{typeLabel}</span>
                    <span className="ms-auto text-xs text-slate-500">
                      {r.start_date} → {r.end_date} ({r.workflow_state})
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
